import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import type { ScheduleTab } from '../../models/schedule.types'
import { DATE_DIMENSIONS } from '../dateDimensions'

import { checkReachFrequency } from './reachFrequency'

// Port of the v3 `getPartitionColumns` + `setupTabs` derivation (`DataVisible.js:46-385`), minus
// the two responsibilities v4 already owns elsewhere: catalog lookup and ordering are
// `service/mapColumns.ts`, and the field-set seeding landed with ORP-19849 in `sectionFields.ts`
// (which now delegates here). What this module adds over that seeding is everything that needed a
// signal `sectionFields` did not have: the request filters, the catalog, and the caller's surface.
//
// Purity: v3 read `useColumnStore()` (`:115,:219`) and `useWorkspaceStore()` (`:220`) directly, and
// decided the account-centre case by sniffing `window.location.pathname` (`:375`). All three are
// inputs here — `getColumn`, `tabs`, and `isAccountCenter` — per the ticket's boundary AC.

/** v3 `DataVisible.js:10-31` — partition columns that exist on the wire but are never offered. */
export const BLACKLIST_DIMENSIONS = new Set([
  'is_your_domain',
  'ad_snapshot_url',
  'dimension',
  'date_interval',
  'video_url',
  'n',
  'video_id',
  'image_id',
  'audience_id',
  'creative_id',
  'product_offer_id',
  'account_id',
  'adgroup_id',
  'channel',
  'marketing_channel',
  'campaign_id',
  'ad_group_id',
  'workspace_id',
  'asset_id',
  'store_type',
])

/**
 * v3 `DataVisible.js:33-42` — fields that stay offered and choosable whatever a partition
 * enumerates. Media and the synthetic date dimensions are derived rather than stored, so a tab
 * legitimately omits them; reach/frequency are computed live (see `reachFrequency.ts`).
 */
const ALWAYS_ELIGIBLE = new Set<string>([
  ...DATE_DIMENSIONS,
  'thumbnail_url',
  'media',
  'hotel_image_url',
  'image_name',
  'image_url',
  'reach',
  'frequency',
])

/** v3 `DataVisible.js:44` — catalog sub-types a tab may omit without becoming ineligible. */
const ELIGIBLE_MISSING_SUB_TYPES = new Set(['meta', 'status'])

/**
 * v3 `DataVisible.js:259` — `date` and `workspace_id` never disqualify a tab. Both are injected
 * rather than enumerated, so requiring them would make every partition ineligible.
 */
const UNREQUIRED_DIMENSIONS = new Set(['date', 'workspace_id'])

export interface PartitionDateRange {
  first: Date | null
  last: Date | null
}

export interface PartitionColumnsInput {
  /** Every schedule tab matched for the target partition (v3 `partitionTabs`, `:267-276`). */
  tabs: readonly ScheduleTab[]
  /** Pre-selected dimensions — what renders by default, not the whole universe. */
  dimensions?: readonly string[]
  /** Pre-selected metrics. */
  metrics?: readonly string[]
  /** The partition being requested; gates the reach/frequency injection. */
  partitionName?: string | null | undefined
  /** Request filters; the reach/frequency injection reads `marketing_channel` off these. */
  filters?: readonly ReportFilter[] | undefined
  /** v3 `filterCols` — fields supplied by a filter, so a tab omitting them stays eligible. */
  filterFields?: readonly string[]
  /** v3 `addCols` — caller-forced additions that bypass tab enumeration entirely. */
  addFields?: readonly string[]
  /** v3 `disableControl` — skip tab eligibility; every matched tab contributes. */
  disableControl?: boolean
  /** Replaces v3's `window.location.pathname.includes('account-center')` sniff (`:375`). */
  isAccountCenter?: boolean
  /** v3 `wsIds` — the account-centre `workspace_id` column needs a multi-workspace request. */
  workspaceIds?: readonly (number | string)[]
  /** Catalog accessor for the `sub_type` eligibility escape hatch (v3 `columnsStore`, `:283`). */
  getColumn?: (field: string) => ReportColumn | null | undefined
}

export interface PartitionColumnsResult {
  /** The offer universe, in v3's order: pre-selected, then tab columns, then the injections. */
  fields: string[]
  /**
   * Offered but not choosable — v3 set `disabled: true` / `selected: false` on these (`:333-338`).
   * A field lands here when it is enumerated only by tabs the request disqualified.
   *
   * Pre-selected fields are never listed. v3 disabled them too unless `activeAll` was set, but
   * there the case could not arise: v3 only ever built columns from the enumerated union, so a
   * pre-selected field no tab carries had no column to disable. v4 always offers the pre-selected
   * set (ORP-19849), so the case is reachable — and disabling a field that is currently rendering
   * would drop it, which is the regression ORP-19849 exists to prevent. `activeAll` is therefore
   * not a parameter: it had exactly one defensible value here.
   */
  disabledFields: string[]
  /** `[first, last]` availability per ELIGIBLE tab (v3 `tabDates`, `:305`). */
  dateRanges: PartitionDateRange[]
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * v3 `canUseMissingColumn` (`:279-287`). A tab that omits one of the requested fields is normally
 * disqualified — the report cannot be grouped on a column the partition does not carry. These are
 * the exemptions: derived fields (`prev_*`, `*_change`) that only ever exist after the response,
 * the always-eligible set, and catalog `meta`/`status` columns, which every partition can join.
 */
function canUseMissingColumn(
  field: string,
  getColumn: PartitionColumnsInput['getColumn'],
  cache: Map<string, boolean>,
): boolean {
  if (ALWAYS_ELIGIBLE.has(field) || field.startsWith('prev_') || field.endsWith('_change')) {
    return true
  }
  const cached = cache.get(field)
  if (cached !== undefined) return cached

  const subType = getColumn?.(field)?.sub_type
  const allowed = subType !== undefined && ELIGIBLE_MISSING_SUB_TYPES.has(subType)
  cache.set(field, allowed)
  return allowed
}

/**
 * Resolve a partition's offer universe, its disabled subset and its availability windows.
 *
 * Returns the pre-selected set unchanged when no tab enumerates columns: without metadata there is
 * no universe to widen to, and inventing one would offer fields the report cannot serve. This is
 * ORP-19849's contract and the reason the injections below sit inside the enumerated branch only.
 */
export function resolvePartitionColumns(input: PartitionColumnsInput): PartitionColumnsResult {
  const dimensions = input.dimensions ?? []
  const metrics = input.metrics ?? []
  const preselected = [...dimensions, ...metrics]
  const enumerated = input.tabs.flatMap((tab) => stringArray(tab.columns))

  if (enumerated.length === 0) {
    return { fields: [...new Set(preselected)], disabledFields: [], dateRanges: [] }
  }

  // v3 `:258-262`. Fields a filter already pins are dropped: the filter narrows the request to rows
  // that carry them, so the tab need not enumerate the column for the request to be answerable.
  const filterFields = new Set(input.filterFields ?? [])
  const required = new Set(
    [...dimensions.filter((field) => !UNREQUIRED_DIMENSIONS.has(field)), ...metrics].filter(
      (field) => !filterFields.has(field),
    ),
  )
  const needCheck = required.size > 0 && input.disableControl !== true
  const subTypeCache = new Map<string, boolean>()

  // Every matched tab contributes to the OFFER (`allCols`, v3 `:302` — before the eligibility
  // `continue`); only eligible tabs contribute to the CHOOSABLE set and the date windows.
  const choosable = new Set<string>()
  const dateRanges: PartitionDateRange[] = []
  for (const tab of input.tabs) {
    const columns = stringArray(tab.columns)
    const eligible =
      !needCheck ||
      [...required].every(
        (field) =>
          columns.includes(field) || canUseMissingColumn(field, input.getColumn, subTypeCache),
      )
    if (!eligible) continue

    dateRanges.push({
      first: parseDate(tab.first_available_date),
      last: parseDate(tab.last_available_date),
    })
    for (const field of columns) choosable.add(field)
  }

  const offered = new Set(preselected)
  for (const field of enumerated) {
    if (!BLACKLIST_DIMENSIONS.has(field)) offered.add(field)
  }
  for (const field of DATE_DIMENSIONS) offered.add(field)
  for (const field of input.addFields ?? []) offered.add(field)

  // v3 `:316-320`. Reach and frequency are never enumerated by a partition — the backend computes
  // them live — so they are offered only when the request has narrowed to a channel that serves them.
  if (checkReachFrequency(input.partitionName, input.filters)) {
    offered.add('reach')
    offered.add('frequency')
  }

  // v3 `:371-383`. The multi-workspace "Workspace" column, blacklisted everywhere else, is the one
  // way an account-centre report can tell whose rows it is showing. v3 unshifts it AFTER the
  // disable pass, so — like the date dimensions — it is synthetic and never disabled.
  const accountCentreWorkspace =
    input.isAccountCenter === true && (input.workspaceIds?.length ?? 0) > 0
  if (accountCentreWorkspace) offered.add('workspace_id')

  const preselectedSet = new Set(preselected)
  const fields = [...offered]
  const disabledFields = fields.filter(
    (field) =>
      !choosable.has(field) &&
      !preselectedSet.has(field) &&
      !ALWAYS_ELIGIBLE.has(field) &&
      !(accountCentreWorkspace && field === 'workspace_id'),
  )

  return { fields, disabledFields, dateRanges }
}
